import React, { useEffect, useMemo, useState } from "react";
import { MapPin, Save, Users, AlertTriangle, Info, Ghost } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { Spinner } from "./common";
import { api, errMsg } from "../lib/api";
import { staffLabelText } from "./StaffAvatar";
import { TerritoryImpact } from "./TerritoryImpact";

// Territory assignment dialog.
// Supervisors -> one/multiple ZONES (admin only). Phase 166: a zone can be DIVIDED among supervisors — tick the zone
//   to hold it whole, or expand it and tick specific wards to hold only those. A whole-zone holder automatically
//   loses the wards another supervisor holds explicitly (carve-out); wards of a split zone nobody holds are admin-only.
// Tax Collectors -> WARDS (within the mapped supervisor's effective wards) and, optionally, specific MOHALLAS inside them.
//
// Ownership is resolved at the MOHALLA level (Phase 158):
//   * ward ticked, no mohallas ticked  -> the collector covers the WHOLE ward
//   * ward ticked + some mohallas      -> the collector covers ONLY those mohallas; the ward tick is just the parent
//                                         selector, so other collectors can take the remaining mohallas freely
// Overlap warnings follow the same rule: whole-ward vs whole-ward, or the same mohalla held by two collectors.
// A whole-ward collector alongside a mohalla-level one is NOT an overlap — the mohalla is carved out of the ward.
export function TerritoryDialog({ code, user, open, onOpenChange, onSaved, presetWardIds = null, presetWardName = null }) {
  const [data, setData] = useState(null);
  const [zoneIds, setZoneIds] = useState([]);
  const [wardIds, setWardIds] = useState([]);
  const [mohallaIds, setMohallaIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [overlaps, setOverlaps] = useState(null); // list of {label, names} pending confirmation
  const [impact, setImpact] = useState(null);     // Phase 156: live preview of what the save will do to assignments
  const [openZones, setOpenZones] = useState([]); // Phase 166: zones whose ward list is expanded (supervisor mode)
  const [preset, setPreset] = useState(null);     // Phase 167: ward pre-ticked from a placeholder row ("Assign real collector")

  useEffect(() => {
    if (!open || !user?.id) return;
    setData(null);
    setPreset(null);
    api.get(`/t/${code}/users/${user.id}/territory`)
      .then((r) => {
        setData(r.data);
        setZoneIds(r.data.zone_ids || []);
        const base = r.data.ward_ids || [];
        // Phase 167: a ward handed over from a zone placeholder arrives pre-ticked (whole ward)
        const wanted = (presetWardIds || []).filter((id) => !base.includes(id));
        const allowed = wanted.filter((id) => (r.data.wards || []).some((w) => w.id === id));
        if (r.data.user?.role !== "supervisor" && allowed.length) {
          setWardIds([...base, ...allowed]);
          setPreset({ ids: allowed, name: presetWardName || (r.data.wards.find((w) => w.id === allowed[0]) || {}).name });
        } else {
          setWardIds(base);
          if (wanted.length && r.data.user?.role !== "supervisor") {
            toast.warning(`${presetWardName || "That ward"} is outside this collector's supervisor territory`, {
              description: "Pick a collector who reports to the supervisor of that ward, or widen the supervisor's territory first.",
            });
          }
        }
        setMohallaIds(r.data.mohalla_ids || []);
        // zones that already have specific wards ticked open expanded
        if (r.data.user?.role === "supervisor") {
          const wz = new Set((r.data.wards || []).filter((w) => (r.data.ward_ids || []).includes(w.id)).map((w) => w.zone_id));
          setOpenZones([...wz]);
        }
      })
      .catch((e) => { toast.error(errMsg(e)); onOpenChange(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id, code]);

  const isSupervisor = data?.user?.role === "supervisor";

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  // ---- Phase 166: supervisor zone → wards helpers
  const wardsByZone = useMemo(() => {
    if (!isSupervisor) return {};
    const map = {};
    (data?.wards || []).forEach((w) => { (map[w.zone_id] = map[w.zone_id] || []).push(w); });
    return map;
  }, [data, isSupervisor]);
  const pickedByZone = useMemo(() => {
    const map = {};
    const ws = new Set(wardIds);
    Object.entries(wardsByZone).forEach(([zid, list]) => { map[zid] = list.filter((w) => ws.has(w.id)).map((w) => w.id); });
    return map;
  }, [wardsByZone, wardIds]);
  const isWholeZone = (zid) => zoneIds.includes(zid) && (pickedByZone[zid] || []).length === 0;
  const toggleZone = (zid) => {
    if (zoneIds.includes(zid)) {
      setZoneIds(zoneIds.filter((x) => x !== zid));
      const zw = (wardsByZone[zid] || []).map((w) => w.id);
      setWardIds(wardIds.filter((w) => !zw.includes(w)));
      setOpenZones(openZones.filter((x) => x !== zid));
    } else {
      setZoneIds([...zoneIds, zid]);
    }
  };
  const setZoneWards = (zid, all) => {
    const zw = (wardsByZone[zid] || []).map((w) => w.id);
    const rest = wardIds.filter((w) => !zw.includes(w));
    setWardIds(all ? [...rest, ...zw] : rest);
  };
  // wards of a zone I hold BY WARD that nobody (me, an explicit holder or a whole-zone holder) will see → admin-only
  const unownedPreview = useMemo(() => {
    if (!isSupervisor) return [];
    const out = [];
    (data?.zones || []).forEach((z) => {
      if (!zoneIds.includes(z.id) || isWholeZone(z.id)) return;
      if ((z.mapped_to || []).length > 0) return;   // another supervisor still holds the zone whole → they keep the rest
      (wardsByZone[z.id] || []).forEach((w) => {
        if (!wardIds.includes(w.id) && (w.mapped_to || []).length === 0 && w.is_active !== false) out.push({ ...w, zone_name: z.name });
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupervisor, data, zoneIds, wardIds, wardsByZone]);

  const mohallasByWard = useMemo(() => {
    const map = {};
    (data?.mohallas || []).forEach((m) => { (map[m.ward_id] = map[m.ward_id] || []).push(m); });
    return map;
  }, [data]);

  // mohallas of each ward that are currently ticked -> decides whole-ward vs mohalla-level coverage per ward
  const selectedByWard = useMemo(() => {
    const map = {};
    const mset = new Set(mohallaIds);
    Object.entries(mohallasByWard).forEach(([wid, list]) => { map[wid] = list.filter((m) => mset.has(m.id)).map((m) => m.id); });
    return map;
  }, [mohallasByWard, mohallaIds]);
  const isWholeWard = (wid) => wardIds.includes(wid) && (selectedByWard[wid] || []).length === 0;

  const toggleWard = (id) => {
    if (wardIds.includes(id)) {
      setWardIds(wardIds.filter((x) => x !== id));
      // drop mohallas of the removed ward
      const wardMohallas = (mohallasByWard[id] || []).map((m) => m.id);
      setMohallaIds(mohallaIds.filter((m) => !wardMohallas.includes(m)));
    } else {
      setWardIds([...wardIds, id]);
    }
  };
  const setWardMohallas = (wid, all) => {
    const wardMohallas = (mohallasByWard[wid] || []).map((m) => m.id);
    const rest = mohallaIds.filter((m) => !wardMohallas.includes(m));
    setMohallaIds(all ? [...rest, ...wardMohallas] : rest);
  };

  // Chip: who else is here. `tone` amber = a real overlap with the current selection, muted = information only.
  const MappedTo = ({ names, tone = "amber", icon: Icon = Users, prefix = "", title, testId }) => {
    if (!names || names.length === 0) return null;
    const shown = names.slice(0, 2).join(", ");
    const extra = names.length > 2 ? ` +${names.length - 2}` : "";
    const cls = tone === "amber"
      ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
      : "bg-muted text-muted-foreground";
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium min-w-0 max-w-full ${cls}`}
        title={title || `${prefix || "Already mapped to"}: ${names.join(", ")}`} data-testid={testId} data-tone={tone}>
        <Icon className="h-3 w-3 shrink-0" /> <span className="truncate min-w-0">{prefix ? `${prefix} ` : ""}{shown}{extra}</span>
      </span>
    );
  };

  // Right-aligned group of chips that wraps onto its own line when the row is tight.
  const Chips = ({ children }) => (
    <span className="ml-auto flex flex-wrap items-center justify-end gap-1 min-w-0 max-w-full">{children}</span>
  );

  const doSave = async () => {
    setBusy(true);
    try {
      const body = isSupervisor ? { zone_ids: zoneIds, ward_ids: wardIds } : { ward_ids: wardIds, mohalla_ids: mohallaIds };
      const r = await api.put(`/t/${code}/users/${user.id}/territory`, body);
      const moved = r.data?.reparented || [];
      const movedText = moved.length
        ? `${moved.length} collector${moved.length === 1 ? "" : "s"} now report${moved.length === 1 ? "s" : ""} to ${isSupervisor ? "the supervisor owning their wards" : moved[0]?.to?.name || "the ward owner"}: ${moved.slice(0, 3).map((m) => m.name).join(", ")}${moved.length > 3 ? ` +${moved.length - 3}` : ""}.`
        : "";
      const unowned = r.data?.unowned_wards || [];
      if (isSupervisor && unowned.length) {
        toast.warning(`${unowned.length} ward${unowned.length === 1 ? "" : "s"} now have no supervisor`, {
          description: `${unowned.slice(0, 4).map((w) => w.name).join(", ")}${unowned.length > 4 ? ` +${unowned.length - 4}` : ""} — visible to the admin only until mapped to a supervisor.`,
        });
      }
      if (r.data?.assignment_sync === "started") {
        toast.success(`Territory saved for ${user.name}`, {
          description: `${movedText ? movedText + " " : ""}Property assignments in ${r.data.assignment_sync_wards || ""} ward${r.data.assignment_sync_wards === 1 ? "" : "s"} are being updated to match — unassigned and stranded properties move to whoever covers them.`,
        });
      } else {
        toast.success(`Territory saved for ${user.name}`, movedText ? { description: movedText } : undefined);
      }
      setOverlaps(null);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const save = () => {
    // warn only on REAL overlaps: zone vs zone (supervisors), whole ward vs whole ward, or the same mohalla twice
    const found = [];
    if (isSupervisor) {
      (data?.zones || []).forEach((z) => {
        if (!zoneIds.includes(z.id)) return;
        const zoneLabel = /zone/i.test(z.name || "") ? z.name : `Zone ${z.name}`;
        if (isWholeZone(z.id)) {
          // whole zone vs another WHOLE-zone holder = overlap (ward-level holders are carved out, not shared)
          if ((z.mapped_to || []).length > 0) found.push({ label: `${zoneLabel} (whole zone)`, names: z.mapped_to });
        } else {
          // the same ward held explicitly by someone else = overlap (a whole-zone holder simply loses the ward)
          (wardsByZone[z.id] || []).forEach((w) => {
            if (wardIds.includes(w.id) && (w.mapped_to || []).length > 0)
              found.push({ label: `${w.name} (ward · ${zoneLabel})`, names: w.mapped_to });
          });
        }
      });
    } else {
      (data?.wards || []).forEach((w) => {
        if (!wardIds.includes(w.id)) return;
        const wardLabel = /ward/i.test(w.name || "") ? w.name : `Ward ${w.name}`;
        if (isWholeWard(w.id)) {
          // whole ward: clashes only with other WHOLE-ward collectors (mohalla-level ones are carved out, not shared)
          if ((w.mapped_to || []).length > 0) found.push({ label: `${wardLabel} (whole ward)`, names: w.mapped_to });
        } else {
          (mohallasByWard[w.id] || []).forEach((m) => {
            if (mohallaIds.includes(m.id) && (m.mapped_to || []).length > 0)
              found.push({ label: `${m.name} (mohalla · ${wardLabel})`, names: m.mapped_to });
          });
        }
      });
    }
    const stranded = !isSupervisor ? (impact?.lose?.stranded || 0) + (impact?.coverage?.new_alerts || []).reduce((n, a) => n + (a.stranded_no_mohalla || 0), 0) : 0;
    if (found.length > 0 || stranded > 0) {
      setOverlaps(found);
      return;
    }
    doSave();
  };
  const strandWarning = !isSupervisor && impact ? {
    stranded: impact.lose?.stranded || 0,
    alerts: impact.coverage?.new_alerts || [],
  } : null;
  const hasStrand = !!strandWarning && (strandWarning.stranded > 0 || strandWarning.alerts.length > 0);
  const wholeCount = wardIds.filter((w) => isWholeWard(w)).length;
  const partialCount = wardIds.length - wholeCount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="territory-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 text-base">
            <MapPin className="h-4 w-4" /> Territory — {staffLabelText(user?.name, user?.employee_id)}
          </DialogTitle>
        </DialogHeader>
        {!data ? <Spinner /> : isSupervisor ? (
          <div className="space-y-3 min-w-0">
            <p className="text-xs text-muted-foreground" data-testid="territory-help">
              Tick a zone to hold it <b>whole</b>. To divide a zone among supervisors, open it and tick only the wards this
              supervisor should see — every other view (collectors, properties, collections, reports) follows those wards.
              A whole-zone supervisor automatically loses the wards you give to someone else. Amber chips mark a real
              overlap with your selection; grey chips are information only.
            </p>
            <div className="max-h-[50vh] overflow-y-auto overscroll-contain border rounded-lg" data-testid="territory-zones-scroll">
              <div className="p-2 space-y-1">
                {data.zones.map((z) => {
                  const ticked = zoneIds.includes(z.id);
                  const list = (wardsByZone[z.id] || []).filter((w) => w.is_active !== false || wardIds.includes(w.id));
                  const picked = (pickedByZone[z.id] || []).length;
                  const whole = ticked && picked === 0;
                  const expanded = ticked && (openZones.includes(z.id) || picked > 0);
                  const wholeOthers = z.mapped_to || [];
                  const partlyOthers = (z.partly_mapped_to || []).map((p) => `${p.name} (${p.wards})`);
                  const carvedHere = whole ? list.filter((w) => (w.mapped_to || []).length > 0) : [];
                  return (
                    <div key={z.id} data-testid={`territory-zone-row-${z.name}`} data-coverage={ticked ? (whole ? "whole" : "wards") : "none"}>
                      <label className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer text-sm font-medium min-w-0"
                        data-testid={`territory-zone-${z.name}`}>
                        <Checkbox checked={ticked} onCheckedChange={() => toggleZone(z.id)} />
                        <span className="shrink-0">{z.name}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0 whitespace-nowrap">{z.ward_count} ward{z.ward_count === 1 ? "" : "s"}</Badge>
                        {ticked && (
                          <Badge variant="outline" className={`text-[10px] shrink-0 whitespace-nowrap ${whole ? "bg-primary/10 text-primary border-primary/20" : "bg-sky-50 text-sky-800 border-sky-200"}`}
                            data-testid={`territory-zone-coverage-${z.name}`}>
                            {whole
                              ? (carvedHere.length ? `Whole zone − ${carvedHere.length} ward${carvedHere.length === 1 ? "" : "s"} held by others` : "Whole zone")
                              : `${picked} of ${list.length} ward${list.length === 1 ? "" : "s"}`}
                          </Badge>
                        )}
                        <Chips>
                          {/* whole-zone holders: an overlap only if THIS supervisor also takes the whole zone */}
                          <MappedTo names={wholeOthers} tone={whole ? "amber" : "muted"}
                            prefix={whole ? "also whole zone:" : "whole zone:"}
                            title={whole
                              ? `Also holds the whole zone: ${wholeOthers.join(", ")} — both will see the same wards`
                              : `Holds the whole zone: ${wholeOthers.join(", ")}${ticked ? " — the wards you tick are carved out of their zone" : ""}`}
                            testId={`territory-zone-mapped-${z.name}`} />
                          {/* ward-level holders never block the zone — information only */}
                          <MappedTo names={partlyOthers} tone="muted" icon={Info} prefix="by ward:"
                            title={`Holds some wards of this zone: ${partlyOthers.join(", ")} — ${whole ? "those wards are carved out of this supervisor's zone" : "pick other wards or the whole zone freely"}`}
                            testId={`territory-zone-partly-${z.name}`} />
                        </Chips>
                      </label>
                      {ticked && list.length > 0 && (
                        <div className="ml-8 space-y-0.5 pb-1">
                          <div className="flex items-center gap-2 px-2 text-[10px] text-muted-foreground">
                            <span>{whole ? "Covering every ward (minus any held by another supervisor)." : "Covering only the ticked wards."}</span>
                            {!expanded ? (
                              <button type="button" className="ml-auto underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                                onClick={() => setOpenZones([...openZones, z.id])} data-testid={`territory-zone-choose-wards-${z.name}`}>
                                Choose specific wards
                              </button>
                            ) : (
                              <button type="button" className="ml-auto underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                                onClick={() => { if (picked > 0) setZoneWards(z.id, false); else setOpenZones(openZones.filter((x) => x !== z.id)); }}
                                data-testid={`territory-zone-toggle-all-${z.name}`}>
                                {picked > 0 ? "Clear wards (whole zone)" : "Hide wards"}
                              </button>
                            )}
                          </div>
                          {expanded && list.map((w) => {
                            const on = wardIds.includes(w.id);
                            const explicit = w.mapped_to || [];
                            return (
                              <label key={w.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1 rounded-md hover:bg-muted cursor-pointer text-xs text-muted-foreground min-w-0"
                                data-testid={`territory-zone-ward-${w.name}`}>
                                <Checkbox checked={on} onCheckedChange={() => toggle(wardIds, setWardIds, w.id)} />
                                <span className={`shrink-0 ${w.is_active === false ? "line-through" : ""}`}>{w.name}</span>
                                <Chips>
                                  {/* the same ward held explicitly by someone else = real overlap (amber once you tick it) */}
                                  <MappedTo names={explicit} tone={on ? "amber" : "muted"}
                                    title={`Held by: ${explicit.join(", ")}${on ? " — both will see this ward" : ""}`}
                                    testId={`territory-zone-ward-mapped-${w.name}`} />
                                  {/* a whole-zone holder elsewhere: ticking this ward carves it out of their zone, no overlap */}
                                  {explicit.length === 0 && wholeOthers.length > 0 && (
                                    <MappedTo names={wholeOthers} tone="muted" icon={Info} prefix="via whole zone:"
                                      title={`${wholeOthers.join(", ")} hold${wholeOthers.length === 1 ? "s" : ""} this zone whole — ticking this ward moves it to this supervisor`}
                                      testId={`territory-zone-ward-via-zone-${w.name}`} />
                                  )}
                                </Chips>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {data.zones.length === 0 && <p className="text-sm text-muted-foreground p-2">No zones defined in Master Data yet.</p>}
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="territory-selection-summary">
              {zoneIds.length} zone{zoneIds.length === 1 ? "" : "s"} selected
              {zoneIds.length > 0 && <> — {zoneIds.filter((z) => isWholeZone(z)).length} whole, {zoneIds.filter((z) => !isWholeZone(z)).length} by ward ({wardIds.length} ward{wardIds.length === 1 ? "" : "s"})</>}
            </p>
            {data.effective?.carved_ward_ids?.length > 0 && (
              <p className="text-xs text-muted-foreground rounded-lg border bg-muted/40 px-3 py-2" data-testid="territory-carved-notice">
                Today this supervisor does not see {data.effective.carved_ward_ids.length} ward{data.effective.carved_ward_ids.length === 1 ? "" : "s"} of their whole zone{data.effective.whole_zone_ids?.length === 1 ? "" : "s"} because another supervisor holds {data.effective.carved_ward_ids.length === 1 ? "it" : "them"} explicitly:{" "}
                {Object.entries(data.effective.carved_by || {}).slice(0, 5).map(([wid, names]) => `${(data.wards.find((w) => w.id === wid) || {}).name || "ward"} → ${names.join(", ")}`).join("; ")}.
              </p>
            )}
            {unownedPreview.length > 0 && (
              <div className="rounded-lg border border-amber-300/70 bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2 text-xs space-y-1" data-testid="territory-unowned-warning">
                <p className="flex items-center gap-1.5 font-medium text-amber-900 dark:text-amber-300"><AlertTriangle className="h-3.5 w-3.5" /> {unownedPreview.length} ward{unownedPreview.length === 1 ? "" : "s"} will have no supervisor</p>
                <p className="text-muted-foreground">
                  {unownedPreview.slice(0, 6).map((w) => `${w.name} (${w.zone_name})`).join(", ")}{unownedPreview.length > 6 ? ` +${unownedPreview.length - 6}` : ""} — nobody except the admin will see
                  {unownedPreview.length === 1 ? " it" : " them"} until mapped to a supervisor (Unassigned Wards lists them).
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 min-w-0">
            {preset && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs flex items-start gap-2" data-testid="territory-preset-notice">
                <Ghost className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700" aria-hidden="true" />
                <span><b>{preset.name || "The ward"}</b> is ticked for {user?.name} (whole ward). Save to map it — the ward's receipts, visits and
                  property assignments move from the zone placeholder to {user?.name} automatically.</span>
              </div>
            )}
            <p className="text-xs text-muted-foreground" data-testid="territory-help">
              Tick a ward to open its mohallas. Leave every mohalla unticked to cover the <b>whole ward</b>; tick specific
              mohallas to cover <b>only those</b> — other collectors can then take the remaining mohallas of the same ward.
              Amber chips mark a real overlap with your selection; grey chips are information only.
              {(data.supervisor_ward_ids || data.supervisor_zone_ids?.length > 0) && " Wards are limited to the supervisor's territory (their wards of a divided zone, or whole zones)."}
            </p>
            <div className="max-h-[50vh] overflow-y-auto overscroll-contain border rounded-lg" data-testid="territory-wards-scroll">
              <div className="p-2 space-y-1">
                {data.wards.map((w) => {
                  const ticked = wardIds.includes(w.id);
                  const list = mohallasByWard[w.id] || [];
                  const picked = (selectedByWard[w.id] || []).length;
                  const whole = ticked && picked === 0;
                  const wholeOthers = w.mapped_to || [];
                  const partlyOthers = (w.partly_mapped_to || []).map((p) => `${p.name} (${p.mohallas})`);
                  return (
                    <div key={w.id} data-testid={`territory-ward-row-${w.name}`} data-coverage={ticked ? (whole ? "whole" : "mohallas") : "none"}>
                      <label className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer text-sm font-medium min-w-0"
                        data-testid={`territory-ward-${w.name}`}>
                        <Checkbox checked={ticked} onCheckedChange={() => toggleWard(w.id)} />
                        <span className="shrink-0">{w.name}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0 whitespace-nowrap">{w.zone_name || "—"}</Badge>
                        {ticked && (
                          <Badge variant="outline" className={`text-[10px] shrink-0 whitespace-nowrap ${whole ? "bg-primary/10 text-primary border-primary/20" : "bg-sky-50 text-sky-800 border-sky-200"}`}
                            data-testid={`territory-ward-coverage-${w.name}`}>
                            {whole ? "Whole ward" : `${picked} of ${list.length} mohalla${list.length === 1 ? "" : "s"}`}
                          </Badge>
                        )}
                        <Chips>
                        {/* whole-ward owners: an overlap only if THIS collector also takes the whole ward */}
                        <MappedTo names={wholeOthers} tone={whole ? "amber" : "muted"}
                          prefix={whole ? "also whole ward:" : "whole ward:"}
                          title={whole
                            ? `Also covers the whole ward: ${wholeOthers.join(", ")} — both will see the same properties`
                            : `Covers the whole ward: ${wholeOthers.join(", ")}${ticked ? " — the mohallas you tick are carved out of their ward" : ""}`}
                          testId={`territory-ward-mapped-${w.name}`} />
                        {/* mohalla-level holders never block the ward — information only */}
                        <MappedTo names={partlyOthers} tone="muted" icon={Info} prefix="by mohalla:"
                          title={`Covers some mohallas of this ward: ${partlyOthers.join(", ")} — pick other mohallas or the whole ward freely`}
                          testId={`territory-ward-partly-${w.name}`} />
                        </Chips>
                      </label>
                      {ticked && list.length > 0 && (
                        <div className="ml-8 space-y-0.5 pb-1">
                          <div className="flex items-center gap-2 px-2 text-[10px] text-muted-foreground">
                            <span>{whole ? "Covering every mohalla and properties without one." : "Covering only the ticked mohallas."}</span>
                            <button type="button" className="ml-auto underline underline-offset-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                              onClick={() => setWardMohallas(w.id, picked < list.length)} data-testid={`territory-ward-toggle-all-${w.name}`}>
                              {picked < list.length ? "Tick all mohallas" : "Clear mohallas (whole ward)"}
                            </button>
                          </div>
                          {list.map((m) => {
                            const on = mohallaIds.includes(m.id);
                            const explicit = m.mapped_to || [];
                            return (
                              <label key={m.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1 rounded-md hover:bg-muted cursor-pointer text-xs text-muted-foreground min-w-0"
                                data-testid={`territory-mohalla-${m.name}`}>
                                <Checkbox checked={on} onCheckedChange={() => toggle(mohallaIds, setMohallaIds, m.id)} />
                                <span className="shrink-0">{m.name}</span>
                                <Chips>
                                {/* the same mohalla held by someone else = real overlap (amber once you tick it) */}
                                <MappedTo names={explicit} tone={on ? "amber" : "muted"}
                                  title={`Mapped to: ${explicit.join(", ")}${on ? " — both will see this mohalla's properties" : ""}`}
                                  testId={`territory-mohalla-mapped-${m.name}`} />
                                {/* a whole-ward collector elsewhere: ticking this mohalla takes it over, no overlap */}
                                {explicit.length === 0 && wholeOthers.length > 0 && !whole && (
                                  <MappedTo names={wholeOthers} tone="muted" icon={Info} prefix="via whole ward:"
                                    title={`${wholeOthers.join(", ")} cover${wholeOthers.length === 1 ? "s" : ""} this ward whole — ticking this mohalla moves it to this collector`}
                                    testId={`territory-mohalla-via-ward-${m.name}`} />
                                )}
                                </Chips>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                {data.wards.length === 0 && (
                  <p className="text-sm text-muted-foreground p-2">
                    No wards available — {data.supervisor_zone_ids?.length ? "the supervisor's zones have no wards defined." : "assign zones to the supervisor first or add wards in Master Data."}
                  </p>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="territory-selection-summary">
              {wardIds.length} ward{wardIds.length === 1 ? "" : "s"} selected
              {wardIds.length > 0 && <> — {wholeCount} whole, {partialCount} by mohalla ({mohallaIds.length} mohalla{mohallaIds.length === 1 ? "" : "s"})</>}
            </p>
            <TerritoryImpact code={code} userId={user?.id} wardIds={wardIds} mohallaIds={mohallaIds} enabled={!!data && !isSupervisor} onChange={setImpact} />
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy || !data} data-testid="territory-save-button">
            <Save className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Save territory"}
          </Button>
        </div>

        <AlertDialog open={!!overlaps} onOpenChange={(v) => !v && setOverlaps(null)}>
          <AlertDialogContent data-testid="territory-overlap-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 font-heading text-base">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                {(overlaps || []).length > 0 ? "Territory overlap detected" : "Properties will be left without a collector"}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2">
                  {(overlaps || []).length > 0 && (
                    <>
                      <p className="text-sm">
                        {(overlaps || []).length} selected area{(overlaps || []).length === 1 ? " is" : "s are"} already
                        covered by other staff at the same level. Both will see and work the same properties.
                      </p>
                      <div className="max-h-44 overflow-y-auto rounded-lg border divide-y">
                        {(overlaps || []).map((o) => (
                          <div key={o.label} className="flex items-center justify-between gap-2 px-3 py-2 text-sm"
                            data-testid={`overlap-row-${o.label}`}>
                            <span className="font-medium">{o.label}</span>
                            <span className="text-xs text-muted-foreground text-right">{o.names.join(", ")}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  {hasStrand && (
                    <div className="rounded-lg border border-amber-300/70 bg-amber-50/70 dark:bg-amber-950/20 px-3 py-2 text-sm space-y-1" data-testid="territory-strand-warning">
                      {strandWarning.stranded > 0 && (
                        <p>{strandWarning.stranded.toLocaleString("en-IN")} propert{strandWarning.stranded === 1 ? "y" : "ies"} currently with this collector will be left with <span className="font-medium">no collector who can see {strandWarning.stranded === 1 ? "it" : "them"}</span>.</p>
                      )}
                      {strandWarning.alerts.map((a) => (
                        <p key={a.ward_id}><span className="font-medium">{a.ward_name}</span>: no whole-ward collector will remain, so {a.stranded_no_mohalla.toLocaleString("en-IN")} propert{a.stranded_no_mohalla === 1 ? "y" : "ies"} without a mohalla will have no covering collector.</p>
                      ))}
                      <p className="text-xs text-muted-foreground">Map a whole-ward collector for the ward, or fill the missing mohallas with Mohalla Backfill (Master Data → Mohallas).</p>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Save anyway if this is intentional, or go back and adjust the selection.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="overlap-go-back-button">Go back</AlertDialogCancel>
              <AlertDialogAction onClick={doSave} disabled={busy} data-testid="overlap-save-anyway-button">
                {busy ? "Saving..." : "Save anyway"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
